import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUserContext } from '../../providers/UserProvider';
import { ACTIONS, RESOURCES } from 'permissions/resources';
import {
  isAllowedByPolicy,
  httpMethodToAction,
} from 'permissions/config/policies';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const {
    user,
    role,
    tenants,
    selectedTenant,
    selectedWarehouse,
    isLoading,
    warehouse_permissions,
  } = useUserContext();
  const params = useParams();
  const routeTenantKey = params?.tenant || null; // 문자열 (domain)
  const routeWarehouse = params?.warehouse || null; // 문자열 (domain)

  // 테넌트 키(=첫 domain) 집합
  const tenantKeys = useMemo(
    () =>
      new Set(
        (tenants || []).map(t => t?.domains?.[0]?.domain).filter(Boolean),
      ),
    [tenants],
  );

  const domainsOfTenantKey = useCallback(
    tenantKey => {
      const t = (tenants || []).find(
        tt => tt?.domains?.[0]?.domain === tenantKey,
      );
      return (t?.domains || []).map(d => d?.domain).filter(Boolean);
    },
    [tenants],
  );

  const myWarehouseNames = useMemo(
    () =>
      new Set(
        (warehouse_permissions || [])
          .map(p => p?.warehouse?.domain || p?.warehouse?.name)
          .filter(Boolean),
      ),
    [warehouse_permissions],
  );

  // --- 스코프 체크 ---
  const hasTenantAccess = useCallback(
    tenantKey => {
      if (!tenantKey) return false;
      if (role === 'superuser') return true;
      return tenantKeys.has(tenantKey);
    },
    [role, tenantKeys],
  );

  const hasWarehouseAccess = useCallback(
    (tenantKey, warehouseDomain) => {
      if (!tenantKey || !warehouseDomain) return false;
      if (role === 'superuser') return true;

      const tDomains = new Set(domainsOfTenantKey(tenantKey));
      if (!tDomains.has(warehouseDomain)) return false;

      if (role === 'tenant_admin') return true; // 테넌트 내 전체
      return myWarehouseNames.has(warehouseDomain); // manager/staff
    },
    [role, domainsOfTenantKey, myWarehouseNames],
  );

  // --- 정책 체크 ---
  const can = useCallback(
    (resource, action) => {
      if (!role) return false;
      return isAllowedByPolicy(role, resource, action); // role 이름을 policies와 일치(tenant_admin)시킴
    },
    [role],
  );

  const canHttp = useCallback(
    (resource, method) => {
      const action = httpMethodToAction(method);
      return can(resource, action);
    },
    [can],
  );

  // --- 라우트 보정 (URL이 스코프 밖이면 올바른 조합 반환) ---
  const ensureRouteInScope = useCallback(
    (tenantKey, warehouseDomain) => {
      const tKey = tenantKey || selectedTenant?.key || routeTenantKey || null;
      const wDom =
        warehouseDomain || selectedWarehouse?.domain || routeWarehouse || null;

      if (!tKey) return { ok: false, tenantKey: null, warehouseDomain: null };

      if (!hasTenantAccess(tKey)) {
        // 내 첫 테넌트
        const myFirst = tenants?.[0]?.domains?.[0]?.domain || null;
        if (!myFirst)
          return { ok: false, tenantKey: null, warehouseDomain: null };

        const domList = domainsOfTenantKey(myFirst);
        const forAdmin = domList[0] || null;
        const forMgr = domList.find(d => myWarehouseNames.has(d)) || null;
        const wh =
          role === 'superuser' || role === 'tenant_admin' ? forAdmin : forMgr;

        return { ok: false, tenantKey: myFirst, warehouseDomain: wh };
      }

      if (!wDom || !hasWarehouseAccess(tKey, wDom)) {
        const domList = domainsOfTenantKey(tKey);
        const forAdmin = domList[0] || null;
        const forMgr = domList.find(d => myWarehouseNames.has(d)) || null;
        const wh =
          role === 'superuser' || role === 'tenant_admin' ? forAdmin : forMgr;

        return { ok: false, tenantKey: tKey, warehouseDomain: wh };
      }

      return { ok: true, tenantKey: tKey, warehouseDomain: wDom };
    },
    [
      role,
      selectedTenant,
      selectedWarehouse,
      routeTenantKey,
      routeWarehouse,
      tenants,
      hasTenantAccess,
      hasWarehouseAccess,
      domainsOfTenantKey,
      myWarehouseNames,
    ],
  );

  const value = useMemo(
    () => ({
      user,
      role,
      isLoading,
      selectedTenant,
      selectedWarehouse,
      hasTenantAccess,
      hasWarehouseAccess,
      can,
      canHttp,
      ensureRouteInScope,
      canSeeAdminMenu: can(RESOURCES.PAGE_ADMIN_DASHBOARD, ACTIONS.VIEW),
    }),
    [
      user,
      role,
      isLoading,
      selectedTenant,
      selectedWarehouse,
      hasTenantAccess,
      hasWarehouseAccess,
      can,
      canHttp,
      ensureRouteInScope,
    ],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionContext = () => useContext(PermissionContext);
